import React, { useState, useMemo } from 'react';
import {
  Box,
  Button,
  Flex,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Text,
  Spinner,
  useColorModeValue,
  Badge,
} from '@chakra-ui/react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from 'react-query';
import toast from 'react-hot-toast';
import { FaFileExcel } from 'react-icons/fa';
import { listFormSubmissionsAdmin } from '../services/formService';
import { exportFormSubmissionsToExcel } from '../utils/excelExport';

export default function FormResponses() {
  const { id: formId } = useParams();
  const textColor = useColorModeValue('gray.800', 'white');
  const cardBg = useColorModeValue('white', 'gray.700');
  const [exporting, setExporting] = useState(false);

  const { data, isLoading, isError, error } = useQuery(
    ['form-submissions', formId],
    async () => {
      const res = await listFormSubmissionsAdmin(formId, { page: 1, limit: 100 });
      return res.data?.data;
    },
    { enabled: Boolean(formId) }
  );

  const submissions = data?.submissions || [];
  const formTitle = data?.form?.title || 'پاسخ‌ها';
  const totalCount = data?.pagination?.total ?? submissions.length;

  const fieldKeys = useMemo(() => {
    const formFields = data?.form?.fields || [];
    const rows = data?.submissions || [];
    const keys = [];
    const seen = new Set();
    formFields.forEach((f) => {
      if (f.key && !seen.has(f.key)) {
        seen.add(f.key);
        keys.push(f);
      }
    });
    rows.forEach((s) => {
      if (s.answers && typeof s.answers === 'object') {
        Object.keys(s.answers).forEach((k) => {
          if (!seen.has(k)) {
            seen.add(k);
            keys.push({ key: k, label: k });
          }
        });
      }
    });
    return keys;
  }, [data]);

  const handleExportToExcel = async () => {
    if (!formId) return;
    setExporting(true);
    try {
      let rows = submissions;
      if (totalCount > rows.length) {
        const res = await listFormSubmissionsAdmin(formId, {
          page: 1,
          limit: Math.min(totalCount, 5000),
        });
        rows = res.data?.data?.submissions || rows;
      }
      if (!rows.length) {
        toast.error('پاسخی برای خروجی وجود ندارد');
        return;
      }
      exportFormSubmissionsToExcel(formTitle, fieldKeys, rows);
      toast.success('فایل اکسل با موفقیت دانلود شد');
    } catch (e) {
      console.error('form excel export', e);
      toast.error('خطا در دانلود فایل اکسل');
    } finally {
      setExporting(false);
    }
  };

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={6} flexWrap="wrap" gap={3}>
        <Heading size="md" color={textColor}>
          پاسخ‌های «{formTitle}»
          {totalCount > 0 && (
            <Text as="span" fontSize="sm" fontWeight="normal" color="gray.500" mr={2}>
              ({totalCount} پاسخ)
            </Text>
          )}
        </Heading>
        <Flex gap={2} flexWrap="wrap">
          <Button
            leftIcon={<FaFileExcel />}
            colorScheme="green"
            borderRadius="15px"
            fontSize="sm"
            fontWeight="bold"
            px="20px"
            py="10px"
            onClick={handleExportToExcel}
            isLoading={exporting}
            loadingText="در حال آماده‌سازی..."
            isDisabled={isLoading || totalCount === 0}
          >
            خروجی اکسل
          </Button>
          <Button as={Link} to={`/form-builder/${formId}/edit`} variant="outline" colorScheme="gray">
            ویرایش فرم
          </Button>
          <Button as={Link} to="/form-builder" variant="outline" colorScheme="gray">
            بازگشت
          </Button>
        </Flex>
      </Flex>

      <Box bg={cardBg} borderRadius="xl" p={4} boxShadow="sm" overflowX="auto">
        {isLoading ? (
          <Flex justify="center" py={12}>
            <Spinner />
          </Flex>
        ) : isError ? (
          <Text color="red.500" py={8} textAlign="center">
            {error?.response?.data?.message || 'خطا در بارگذاری پاسخ‌ها'}
          </Text>
        ) : submissions.length === 0 ? (
          <Text color="gray.500" py={8} textAlign="center">
            هنوز پاسخی ثبت نشده است.
          </Text>
        ) : (
          <Table size="sm">
            <Thead>
              <Tr>
                <Th>موبایل (تأییدشده)</Th>
                {fieldKeys.map((f) => (
                  <Th key={f.key} whiteSpace="nowrap">
                    {f.label || f.key}
                  </Th>
                ))}
                <Th>آخرین به‌روزرسانی</Th>
              </Tr>
            </Thead>
            <Tbody>
              {submissions.map((s) => (
                <Tr key={s._id}>
                  <Td>
                    <Badge>{s.phone}</Badge>
                  </Td>
                  {fieldKeys.map((f) => (
                    <Td key={f.key} maxW="220px" whiteSpace="pre-wrap" fontSize="xs">
                      {s.answers?.[f.key] != null ? String(s.answers[f.key]) : '—'}
                    </Td>
                  ))}
                  <Td fontSize="xs">
                    {s.updatedAt ? new Date(s.updatedAt).toLocaleString('fa-IR') : '—'}
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        )}
        {totalCount > submissions.length && (
          <Text fontSize="xs" color="gray.500" mt={3} textAlign="center">
            نمایش {submissions.length} از {totalCount} پاسخ — خروجی اکسل همه را شامل می‌شود.
          </Text>
        )}
      </Box>
    </Box>
  );
}
